/*
 * @format
 * @flow
 */
import React from 'react';
import axios from 'axios';
import { Icons, FlexRow, FlexCol, Checkbox, Button, ButtonGroup, TextField, PhoneNumberField, EmailField, Select, SelectOption, Radio, RadioGroup, StyledModal, ModalActions, Row, Cell, phoneValidator } from 'sarsaparilla';
import isEmpty from 'lodash/isEmpty'
import validator from 'validator';
import { InternalSecurityUtils } from '../../../../ui-internal-account';
import * as globals from '../../constants/globals';
import { AccountProps } from '../../../../ui-internal-pos/src/props/account';
import { COUNTRIES, US_STATES } from '../../../../ui-account/src/constants';

type PropTypes = {
    account?: AccountProps,
    clearCustomer: Function,
    closeModal: Function,
    isOpen: boolean,
    selectCustomer: Function,
    showCancel: boolean,
    showSkip: boolean,
    allowEmailLess: boolean,
    title: string,
    validateAccount?: Function,
    validateAccountErrorMessage: string,
    searchOnly: boolean,
    defaultEmail: string
};

type StateTypes = {
    account: AccountProps | null,
    accounts: AccountProps[] | null,
    createErr: boolean,
    createInProgress: boolean,
    disableBtn: boolean,
    email: string,
    phone: string,
    filter: string,
    newAcctEmail: string,
    newAcctFirstName: string,
    newAcctLastName: string,
    newAcctCellPhone: string,
    notFound: boolean,
    showNewUserForm: boolean,
    showValidationErr: boolean,
    validAccount: boolean,
    emailRequired: boolean
};

const filterTypePhone = "PHONE";
const filterTypeEmail = "EMAIL";

export default class SelectCustomerModal extends React.Component<PropTypes, StateTypes> {
    static defaultProps = {
        isOpen: false,
        title: 'Connect User Account',
        showSkip: true,
        allowEmailLess: true,
        showCancel: false,
        validateAccountErrorMessage: 'This user is unavailable in the current context.',
        searchOnly: false,
        defaultEmail: ''
    };

    state = {
        email: '',
        phone: '',
        country: 'USA',
        address1: '',
        address2: '',
        city: '',
        state: '',
        zip_code: '',
        filter: filterTypeEmail,
        account: null,
        accounts: null,
        notFound: false,
        showNewUserForm: false,
        showValidationErr: false,
        createErr: false,
        createInProgress: false,
        disableBtn: true,
        newAcctFirstName: '',
        newAcctLastName: '',
        newAcctEmail: '',
        newAcctCellPhone: '',
        validAccount: true,
        emailRequired: true,
        addAddress: false
    };

    getCountries() {
        return COUNTRIES.map((value, index) => (
            <option value={value.value} key={`country-${index}`}>
                {value.label}
            </option>
        ));
    }

    getStates() {
        return US_STATES.map((value, index) => (
            <option value={value.value} key={`state-${index}`}>
                {value.label}
            </option>
        ));
    }

    afterOpenModal = () => {
        this.setState({
            email: this.props.defaultEmail,
            phone: '',
            country: 'USA',
            address1: '',
            address2: '',
            city: '',
            state: '',
            zip_code: '',
            account: null,
            createErr: false,
            validAccount: true,
            notFound: false,
            showValidationErr: false,
            addAddress: false
        });
    };

    searchAccounts(email: string, phone: string) {
        let params = {};
        if (email) {
            params = {
                email
            };
        }
        if (phone) {
            params = {
                phone
            }
        }

        axios
            .get(`${globals.INT_ACCOUNT_API_URL}/search`, {
                headers: { Authorization: InternalSecurityUtils.getAuthHeader() },
                params
            })
            .then((resp) => {
                if (resp.data.account.account_id) {
                    const account = resp.data.account;
                    const validAccount = this.props.validateAccount ? this.props.validateAccount(resp.data.account) : true;
                    this.setState({
                        account,
                        accounts: null,
                        disableBtn: !validAccount,
                        validAccount,
                        notFound: false
                    });
                }
                else if (resp.data.results.length > 0) {
                    const accounts = resp.data.results;
                    this.setState({
                        account: null,
                        accounts,
                        notFound: false
                    });
                }
                else {
                    this.setState({ notFound: true });
                }
            })
            .catch(() => {
                this.setState({ notFound: true });
            });
    }

    createAccount() {
        if(!this.state.createInProgress){
            this.setState({ createInProgress: true }, () => {
                const { addAddress, country, address1, address2, city, state, zip_code } = this.state;
                let newEmail = this.state.newAcctEmail
                if (!this.state.emailRequired) {
                    newEmail = `${this.state.newAcctCellPhone.replace(/\D/g, '')}@recreation.gov`;
                }
                const body = {
                    first_name: this.state.newAcctFirstName,
                    last_name: this.state.newAcctLastName,
                    email: newEmail,
                    cell_phone: this.state.newAcctCellPhone
                };
                if (addAddress) {
                    body.home_address = {
                        country,
                        address1,
                        address2,
                        city,
                        state,
                        zip_code
                    }
                }
                const url = `${globals.INT_ACCOUNT_API_URL}/account`;
                axios
                    .post(url, body, {headers: {Authorization: InternalSecurityUtils.getAuthHeader()}})
                    .then((resp) => {
                        this.resetStateAndCloseModal();
                        this.props.selectCustomer(resp.data);
                    })
                    .catch(() => {
                        this.setState({createErr: true, createInProgress: false});
                    });
            });
        }
    }

    skipCustomerSelect = () => {
        if (this.props.account && this.props.account.walkUp) {
            this.resetStateAndCloseModal();
            return;
        }
        axios
            .get(`${globals.INT_ACCOUNT_API_URL}/search`, {
                headers: { Authorization: InternalSecurityUtils.getAuthHeader() },
                params: { email: '' }
            })
            .then((resp) => {
                this.setState(
                    {
                        account: {
                            account_id: resp.data.walk_up_id || resp.data.walk_on_id,
                            walkUp: true
                        }
                    },
                    () => {
                        this.resetStateAndCloseModal();
                        this.props.selectCustomer(this.state.account);
                    }
                );
            })
            .catch(() => {
                this.setState({ notFound: true });
            });
    };

    clearAccount = () => {
        if (this.props.clearCustomer) {
            this.props.clearCustomer();
            this.afterOpenModal();
        }
    };

    resetStateAndCloseModal = () => {
        this.setState({
            email: '',
            phone: '',
            country: 'USA',
            address1: '',
            address2: '',
            city: '',
            state: '',
            zip_code: '',
            filter: filterTypeEmail,
            account: null,
            accounts: null,
            notFound: false,
            showNewUserForm: false,
            showValidationErr: false,
            createErr: false,
            createInProgress: false,
            disableBtn: true,
            newAcctFirstName: '',
            newAcctLastName: '',
            newAcctEmail: '',
            newAcctCellPhone: '',
            validAccount: true,
            emailRequired: true,
            addAddress: false
        }, () => {
            this.props.closeModal()
        })
    }

    handleChange = (e: SyntheticInputEvent<HTMLInputElement>) => {
        const target = e.target;
        const value = target.value;
        const name = target.name;
        this.setState({
            [name]: value
        });
    };

    handleEmailRequirement = (e: SyntheticInputEvent<HTMLInputElement>) => {
        this.setState({
            emailRequired: !e.target.checked
        });
    };

    handleFilterChange = (e: SyntheticInputEvent<HTMLInputElement>) => {
        const filter = e.target.value;
        if (filter === filterTypePhone) {
            this.setState({
                email: '',
                filter: filterTypePhone,
                account: null
            })
        }
        if (filter === filterTypeEmail) {
            this.setState({
                phone: '',
                filter: filterTypeEmail,
                accounts: null
            })
        }
    };

    handleAddressChange = (e) => {
        const addAddress = e.target.checked;
        if (!addAddress) {
            this.setState({
                country: 'USA',
                address1: '',
                address2: '',
                city: '',
                state: '',
                zip_code: '',
            });
        }
        this.setState({
            addAddress
        });
    }

    handleSubmit = () => {
        this.searchAccounts(this.state.email, this.state.phone);
    };

    handleCreateOrSelect = () => {
        this.setState({ disableBtn: true });
        if (this.state.showNewUserForm) {
            this.setState({ showValidationErr: false, createErr: false });
            if (!this.newAccountIsValid()) {
                this.setState({ showValidationErr: true, disableBtn: false });
                return;
            }
            this.createAccount();
        } else {
            this.resetStateAndCloseModal();
            this.props.selectCustomer(this.state.account);
        }
    };

    selectPhoneUser(account: AccountProps) {
        this.resetStateAndCloseModal();
        this.props.selectCustomer(account);
    }

    newAccountIsValid() {
        let valid = false;
        if (this.state.emailRequired) {
            valid = !validator.isEmpty(this.state.newAcctFirstName) && !validator.isEmpty(this.state.newAcctLastName) && validator.isEmail(this.state.newAcctEmail) && (!validator.isEmpty(this.state.newAcctCellPhone) && phoneValidator(this.state.newAcctCellPhone).isValid);
        } else {
            valid = !validator.isEmpty(this.state.newAcctFirstName) && !validator.isEmpty(this.state.newAcctLastName) && (!validator.isEmpty(this.state.newAcctCellPhone) && phoneValidator(this.state.newAcctCellPhone).isValid);
        }
        if (valid && this.state.addAddress) {
            valid = !validator.isEmpty(this.state.country) && !validator.isEmpty(this.state.address1) && !validator.isEmpty(this.state.city)  && !validator.isEmpty(this.state.state) && !validator.isEmpty(this.state.zip_code);
        }
        return valid;
    }
    render() {
        let formBody;
        let btnText = 'Select';
        if (this.state.showNewUserForm) {
            let valErr;
            if (this.state.showValidationErr) {
                valErr = (
                    <div className="rec-notification-error">
                        <span className="rec-notification-icon">
                            <Icons.IconError className="rec-icon-error" />
                        </span>
                        <div className="rec-notification-body">
                            <p>All fields are required.</p>
                        </div>
                    </div>
                );
            }
            let saveErr;
            if (this.state.createErr) {
                saveErr = (
                    <div className="rec-notification-error">
                        <span className="rec-notification-icon">
                            <Icons.IconError className="rec-icon-error" />
                        </span>
                        <div className="rec-notification-body">
                            <p>Could not create account. An account may already exist with the given email.</p>
                        </div>
                    </div>
                );
            }
            let stateInput = (
                <input
                    data-testid="state-input"
                    id="state"
                    name="state"
                    type="text"
                    value={this.state.state || ''}
                    placeholder="Required"
                    onChange={this.handleChange} />
            );
            if (this.state.country === 'USA') {
                stateInput = (
                    <select
                        data-testid="state-picker"
                        id="state"
                        name="state"
                        value={this.state.state}
                        onChange={this.handleChange}>
                        {this.getStates()}
                    </select>
                );
            }
            btnText = 'Create Account & Continue';
            formBody = (
                <React.Fragment>
                    {valErr}
                    {saveErr}
                    <TextField
                        className="mb-2"
                        label="First Name"
                        id="newacct-first-name-input"
                        type="text"
                        name="newAcctFirstName"
                        value={this.state.newAcctFirstName}
                        placeholder="Required"
                        isRequired
                        onChange={this.handleChange} />
                    <TextField
                        className="mb-2"
                        label="Last Name"
                        id="newacct-last-name-input"
                        type="text"
                        name="newAcctLastName"
                        value={this.state.newAcctLastName}
                        placeholder="Required"
                        isRequired
                        onChange={this.handleChange} />
                    <PhoneNumberField
                        className="mb-2"
                        id="newacct-cell-phone-input"
                        label="Mobile Phone"
                        type="tel"
                        name="newAcctCellPhone"
                        value={this.state.newAcctCellPhone}
                        placeholder="Required"
                        isRequired
                        onChange={this.handleChange} />
                    <EmailField
                        id="newacct-email-input"
                        label="Email"
                        type="text"
                        isRequired={this.state.emailRequired}
                        name="newAcctEmail"
                        value={(this.state.emailRequired ? this.state.newAcctEmail : '')}
                        placeholder={(this.state.emailRequired ? 'Required' : '')}
                        isDisabled={!this.state.emailRequired}
                        onChange={this.handleChange} />
                    {this.state.addAddress && (
                        <>
                            <div className="mt-2 mb-2">
                                <label htmlFor="country" style={{ marginBottom: '0.4rem' }}>
                                    Country
                                    <abbr className="rec-label-required-indicator">
                                        <span aria-hidden="true">*</span>
                                        <span className="rec-sr-only"> (Required) </span>
                                    </abbr>
                                </label>
                                <select
                                    name="country"
                                    id="country"
                                    value={this.state.country}
                                    onChange={this.handleChange}>
                                    {this.getCountries()}
                                </select>
                            </div>
                            <div className="mb-2">
                                <label htmlFor="street-input" style={{ marginBottom: '0.4rem' }}>
                                    Street Address
                                    <abbr className="rec-label-required-indicator">
                                        <span aria-hidden="true">*</span>
                                        <span className="rec-sr-only"> (Required) </span>
                                    </abbr>
                                </label>
                                <input
                                    id="street-input"
                                    name="address1"
                                    type="text"
                                    value={this.state.address1 || ''}
                                    placeholder="Required"
                                    onChange={this.handleChange} />
                            </div>
                            <div className="mb-2">
                                <label htmlFor="apt-input" style={{ marginBottom: '0.4rem' }}>Apt or Suite Number</label>
                                <input id="apt-input" name="address2" type="text" value={this.state.address2 || ''} onChange={this.handleChange} />
                            </div>
                            <div className="mb-2">
                                <label htmlFor="city-input" style={{ marginBottom: '0.4rem' }}>
                                    City
                                    <abbr className="rec-label-required-indicator">
                                        <span aria-hidden="true">*</span>
                                        <span className="rec-sr-only"> (Required) </span>
                                    </abbr>
                                </label>
                                <input
                                    id="city-input"
                                    name="city"
                                    type="text"
                                    value={this.state.city || ''}
                                    placeholder="Required"
                                    onChange={this.handleChange} />
                            </div>
                            <div className="mb-2">
                                <Row>
                                    <Cell className="acct-cell-auto" width={{ xs: 12, sm: 6 }}>
                                        <label htmlFor="state" style={{ marginBottom: '0.4rem' }}>
                                            State/Province
                                            <abbr className="rec-label-required-indicator">
                                                <span aria-hidden="true">*</span>
                                                <span className="rec-sr-only"> (Required) </span>
                                            </abbr>
                                        </label>
                                        {stateInput}
                                    </Cell>
                                    <Cell className="acct-cell-auto" width={{ xs: 12, sm: 6 }}>
                                        <label htmlFor="zip-input" style={{ marginBottom: '0.4rem' }}>
                                            ZIP/Postal Code
                                            <abbr className="rec-label-required-indicator">
                                                <span aria-hidden="true">*</span>
                                                <span className="rec-sr-only"> (Required) </span>
                                            </abbr>
                                        </label>
                                        <input
                                            id="zip-input"
                                            name="zip_code"
                                            type="text"
                                            value={this.state.zip_code || ''}
                                            placeholder="Required"
                                            onChange={this.handleChange} />
                                    </Cell>
                                </Row>
                            </div>
                        </>
                    )}
                    {this.props.allowEmailLess && (
                        <Checkbox
                            className="mt-2"
                            id="email-not-required"
                            label="Customer does not have an email address."
                            value="email-not-required"
                            isChecked={!this.state.emailRequired}
                            onChange={this.handleEmailRequirement} />
                    )}
                    <Checkbox
                        id="addAddress"
                        label="Add customer address."
                        name="addAddress"
                        value="addAddress"
                        isChecked={this.state.addAddress}
                        onChange={this.handleAddressChange} />
                </React.Fragment>
            );
        }
        else {
            formBody = (
                <React.Fragment>
                    {this.state.notFound && (
                        <div className="rec-notification-error" role="alert">
                            <span className="rec-notification-icon">
                                <Icons.IconError className="rec-icon-error" />
                            </span>
                            <div className="rec-notification-body">
                                <div className="rec-notification-title-single-row">Error:</div>
                                <p>User not found.</p>
                            </div>
                        </div>
                    )}
                    {!this.state.validAccount && (
                        <div className="rec-notification-error" role="alert">
                            <span className="rec-notification-icon">
                                <Icons.IconError className="rec-icon-error" />
                            </span>
                            <div className="rec-notification-body">
                                <div className="rec-notification-title-single-row">Error:</div>
                                <p>{this.props.validateAccountErrorMessage}</p>
                            </div>
                        </div>
                    )}
                    <FlexRow className="fs-select-customer-search-fields mb-2">
                        <FlexCol sm={3}>
                            <Select
                                id="filter-by"
                                label="Field to search"
                                isLabelVisible={false}
                                placeholder="Filter By"
                                onChange={this.handleFilterChange}
                                value={this.state.filter}>
                                <SelectOption value={filterTypeEmail}>Email</SelectOption>
                                <SelectOption value={filterTypePhone}>Phone Number</SelectOption>
                            </Select>
                        </FlexCol>
                        {this.state.filter === filterTypeEmail && (
                            <FlexCol>
                                <EmailField
                                    label="Email address to search for"
                                    isLabelVisible={false}
                                    isRequired
                                    id="first-name-input"
                                    type="text"
                                    placeholder="Search email"
                                    name="email"
                                    value={this.state.email}
                                    onChange={this.handleChange} />
                            </FlexCol>
                        )}
                        {this.state.filter === filterTypePhone && (
                            <FlexCol>
                                <PhoneNumberField
                                    label="Phone number to search for"
                                    isLabelVisible={false}
                                    isRequired
                                    id="phone-input"
                                    placeholder="Search phone"
                                    name="phone"
                                    value={this.state.phone}
                                    onChange={this.handleChange} />
                            </FlexCol>
                        )}
                        <FlexCol sm="variable">
                            <Button
                                type="submit"
                                isDisabled={this.state.filter === filterTypeEmail ? isEmpty(this.state.email) : isEmpty(this.state.phone)}
                                appearance="primary"
                                aria-describedby="search-existing-user">
                                Search
                            </Button>
                            <div className="rec-sr-only" id="search-existing-user">
                                Click to search for an existing user by {this.state.filter === filterTypeEmail ? 'email' : 'phone number'}
                            </div>
                        </FlexCol>
                    </FlexRow>
                </React.Fragment>
            );
        }
        let table = null;
        if (this.state.account && this.state.filter === filterTypeEmail && !this.state.showNewUserForm) {
            table = (
                <table className="fs-select-customer-search">
                    <thead>
                        <tr>
                            <th>Name</th>
                            <th>Email</th>
                            <th>Phone Number</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td>
                                {this.state.account.first_name} {this.state.account.last_name}
                                {this.state.account.is_commercial_account && <div className="permit-text-less-emphasis">(Commercial user)</div>}
                            </td>
                            <td>{this.state.account.email}</td>
                            <td>{this.state.account.cell_phone}</td>
                        </tr>
                    </tbody>
                </table>
            );
        }
        if (this.state.accounts && this.state.filter === filterTypePhone && !this.state.showNewUserForm) {
            const tableRows = this.state.accounts.map((account, i) => {
                const key = i
                return (
                    <tr key={key}>
                        <td>
                            {account.first_name} {account.last_name}{' '}
                            {account.is_commercial_account && <div className="permit-text-less-emphasis">(Commercial user)</div>}
                        </td>
                        <td>{account.email}</td>
                        <td>{account.cell_phone}</td>
                        <td>
                            <Button
                                appearance="primary"
                                size="sm"
                                onClick={() => {
                                    this.selectPhoneUser(account)
                                }}>
                                Select
                            </Button>
                        </td>
                    </tr>
                );
            });
            table = (
                <table className="fs-select-customer-search">
                    <thead>
                        <tr>
                            <th>Name</th>
                            <th>Email</th>
                            <th>Phone Number</th>
                            <th>&nbsp;</th>
                        </tr>
                    </thead>
                    <tbody>
                        {tableRows}
                    </tbody>
                </table>
            );
        }

        return (
            <StyledModal
                ariaHideApp={false}
                isOpen={this.props.isOpen}
                onAfterOpen={this.afterOpenModal}
                onRequestClose={this.resetStateAndCloseModal}
                shouldCloseOnOverlayClick={false}
                heading={this.props.title}>
                <form
                    onSubmit={(event) => {
                        event.preventDefault();
                        this.handleSubmit();
                    }}>
                    <RadioGroup
                        isInline
                        isDisabled={this.props.searchOnly}
                        className="mb-2"
                        name="user-type-radios"
                        value={this.state.showNewUserForm ? 'new-user' : 'existing-user'}
                        onChange={() => {
                            this.setState({ showNewUserForm: !this.state.showNewUserForm });
                        }}>
                        <Radio
                            id="existing-user-radio"
                            label="Existing User"
                            value="existing-user" />
                        <Radio
                            id="new-user-radio"
                            label="New User"
                            value="new-user" />
                    </RadioGroup>
                    {formBody}
                    <div aria-live="polite">
                        {table}
                    </div>
                </form>
                <ModalActions>
                    <ButtonGroup>
                        {this.props.showSkip && (
                            <Button
                                appearance="tertiary"
                                onClick={this.skipCustomerSelect}>
                                Skip
                            </Button>
                        )}
                        {this.props.showCancel && (
                            <Button
                                appearance="tertiary"
                                onClick={this.resetStateAndCloseModal}>
                                Close
                            </Button>
                        )}
                        {this.props.showCancel && (
                            <Button
                                appearance="tertiary"
                                onClick={this.clearAccount}>
                                Clear
                            </Button>
                        )}
                        <Button
                            appearance="primary"
                            isDisabled={this.state.createInProgress || this.state.showNewUserForm ? !this.newAccountIsValid() : this.state.disableBtn}
                            onClick={() => {
                                if(this.state.createInProgress){
                                    return;
                                }
                                this.handleCreateOrSelect();
                            }}>
                            {btnText}
                        </Button>
                    </ButtonGroup>
                </ModalActions>
            </StyledModal>
        );
    }
}
